import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Badge, Dropdown, Spinner } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import { withNamespaces } from 'react-i18next';
import Utility from '../../../../GlobalModules/Utility/Utility';
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
    faArrowLeft, 
    faEdit, 
    faFile, 
    faHashtag, 
    faTag, 
    faBuilding, 
    faUser, 
    faCalendar, 
    faAlignLeft, 
    faInfoCircle,
    faDollarSign,
    faCheckCircle
} from "@fortawesome/free-solid-svg-icons";
import { getAccountDetails } from "../Controller/AccountController";
import { connect } from "react-redux";
import '../../Quotation/Assets/css/quotation.scss';

const AccountView = (props) => {
  const { t, i18n } = props;
  const lng = i18n.language;
  const { slack } = useParams();
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch account data based on ID
  useEffect(() => {
    const fetchAccount = async () => {
      try {
        setLoading(true);
        const response = await getAccountDetails(slack);
        if (response.success && response.data) {
          setAccount(response.data);
        } else {
          toast.error(t('accountView.accountNotFound'));
        }
      } catch (error) {
        console.error('Error fetching account:', error);
        toast.error(t('accountView.fetchError'));
      } finally {
        setLoading(false);
      }
    };

    fetchAccount();
  }, [slack]);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  // Render loading state
  if (loading) {
    return (
      <Container fluid className="p-0">
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">{t('accountView.loadingDetails')}</p>
        </div>
      </Container>
    );
  }

  if (!account) {
    return (
      <Container fluid className="p-0">
        <div className="text-center py-5">
          <FontAwesomeIcon icon={faInfoCircle} size="3x" className="text-danger mb-3" />
          <h4>{t('accountView.accountNotFound')}</h4>
          <p>Failed to load account details</p>
          <Link to={`/${lng}/business/accounts`} className="btn btn-primary mt-3">
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            {t('accountView.backToList')}
          </Link>
        </div>
      </Container>
    );
  }

  //======= add action permission ====//
  // Check multiple permissions at once
  const accountsViewPermissions = Utility.hasMultipleActionPermissions(
    'business_account',
    'accounts',
    ['view', 'create', 'edit', 'delete', 'export', 'approve', 'receive', 'generate_invoice', 'change_status', 'accept', 'reject', 'KOT info', 'cancel', 'bill', 'bill_post', 'cancel_bill_post', 'invoice'],
    props.userCredentials.user_details.user_type
  );

  return (
    <Container fluid className='p-0 quotation-view-page'>
      <Row className='top-header-box quotation-view-header mb-3'>
        <Col className='quotation-header-content'>
          <div className='quotation-title-row'>
            <h4 className='quotation-title'>{account.label || 'N/A'}</h4>
            <Badge
              bg={account?.status_data?.value === "1" ? 'success' : 'secondary'}
              className='badgeCustom'
            >
              {account?.status_data?.label || t('accountView.inactive')}
            </Badge>
          </div>
          {/* <div className='quotation-subtitle'>
            <span className='text-muted'>Account Code: {account.account_code || '-'}</span>
          </div> */}
        </Col>
        <Col xs='auto' className='quotation-header-actions'>
          <Link 
            to={`/${lng}/business/accounts`} 
            className='btn btn-outline-secondary me-2'
          >
            <FontAwesomeIcon icon={faArrowLeft} className='me-2' />
            {t('accountView.backToList')}
          </Link>
          {accountsViewPermissions.edit && (
            <Link 
              to={`/${lng}/business/accounts/edit/${account.slack}`} 
              className='btn btn-outline-primary'
            >
              <FontAwesomeIcon icon={faEdit} className='me-2' />
              {t('accountView.edit')}
            </Link>
          )}
        </Col>
      </Row>

      <Row>
        <Col lg={12}>
          <Card className='custom-border-light mb-4 main-content-card quotation-details-card'>
            <Card.Header>
              <h6><FontAwesomeIcon icon={faFile} className='me-2' /> Account Information</h6>
            </Card.Header>
            <Card.Body>
              <div className='quotation-details-grid'>
                <div className='detail-item'>
                  <span className='detail-icon'><FontAwesomeIcon icon={faHashtag} /></span>
                  <div className='detail-content'>
                    <span className='detail-label'>{t('accountView.accountCode')}</span>
                    <span className='detail-value'>{account.account_code || '-'}</span>
                  </div>
                </div>
                <div className='detail-item'>
                  <span className='detail-icon'><FontAwesomeIcon icon={faTag} /></span>
                  <div className='detail-content'>
                    <span className='detail-label'>{t('accountView.accountName')}</span>
                    <span className='detail-value'>{account.label || '-'}</span>
                  </div>
                </div>
                <div className='detail-item'>
                  <span className='detail-icon'><FontAwesomeIcon icon={faBuilding} /></span>
                  <div className='detail-content'>
                    <span className='detail-label'>{t('accountView.accountType')}</span>
                    <span className='detail-value'>{account?.account_type_data?.label || '-'}</span>
                  </div>
                </div>
                <div className='detail-item'>
                  <span className='detail-icon'><FontAwesomeIcon icon={faDollarSign} /></span>
                  <div className='detail-content'>
                    <span className='detail-label'>{t('accountView.initialBalance')}</span>
                    <span className='detail-value'>{formatCurrency(account.initial_balance)}</span>
                  </div>
                </div>
                <div className='detail-item'>
                  <span className='detail-icon'><FontAwesomeIcon icon={faCheckCircle} /></span>
                  <div className='detail-content'>
                    <span className='detail-label'>{t('accountView.posDefaultAccount')}</span>
                    <span className='detail-value'>
                      <Badge bg={account.pos_default === 1 ? 'info' : 'secondary'} className='badgeCustom'>
                        {account.pos_default === 1 ? t('accountView.yes') : t('accountView.no')}
                      </Badge>
                    </span>
                  </div>
                </div>
                <div className='detail-item'>
                  <span className='detail-icon'><FontAwesomeIcon icon={faUser} /></span>
                  <div className='detail-content'>
                    <span className='detail-label'>{t('accountView.createdBy')}</span>
                    <span className='detail-value'>
                      {account?.CreatedUser ? 
                        `${account.CreatedUser.first_name || ''} ${account.CreatedUser.last_name || ''}`.trim() || 'System' 
                        : 'System'}
                    </span>
                  </div>
                </div>
                <div className='detail-item'>
                  <span className='detail-icon'><FontAwesomeIcon icon={faCalendar} /></span>
                  <div className='detail-content'>
                    <span className='detail-label'>{t('accountView.createdOn')}</span>
                    <span className='detail-value'>{Utility.get12HourFormat(account.createdAt) || '-'}</span>
                  </div>
                </div>

                {(() => {
                  const updatedByValue = account?.UpdatedUser ? 
                    `${account.UpdatedUser.first_name || ''} ${account.UpdatedUser.last_name || ''}`.trim() : '';
                  const shouldShowUpdatedBy = updatedByValue && updatedByValue.toLowerCase() !== 'undefined undefined';
                  
                  if (shouldShowUpdatedBy) {
                    return (
                      <div className='detail-item'>
                        <span className='detail-icon'><FontAwesomeIcon icon={faUser} /></span>
                        <div className='detail-content'>
                          <span className='detail-label'>{t('accountView.updatedBy')}</span>
                          <span className='detail-value'>{updatedByValue}</span>
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
                
                {account.updatedAt && (
                  <div className='detail-item'>
                    <span className='detail-icon'><FontAwesomeIcon icon={faCalendar} /></span>
                    <div className='detail-content'>
                      <span className='detail-label'>{t('accountView.updatedOn')}</span>
                      <span className='detail-value'>{Utility.get12HourFormat(account.updatedAt) || '-'}</span>
                    </div>
                  </div>
                )}

                {account.description && (
                  <div className='detail-item detail-item-wide'>
                    <span className='detail-icon'><FontAwesomeIcon icon={faAlignLeft} /></span>
                    <div className='detail-content'>
                      <span className='detail-label'>{t('accountView.description')}</span>
                      <span className='detail-value'>{account.description || '-'}</span>
                    </div>
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

const mapStateToProps = (globalState) => ({
    userCredentials: globalState.LoginReducer.userCredentials,
    token: globalState.LoginReducer.token,
    userEntryType: globalState.LoginReducer.userEntryType
});

export default connect(mapStateToProps)(withNamespaces()(AccountView));